
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ParentApiService } from './parent-api-service';
import { forkJoin, map, mergeMap, Observable } from 'rxjs';
import { EvaluationGenerationDto } from '../entities/evaluation-entities/evaluation-generation-dto';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { RecyclabilityResultDto } from '../entities/evaluation-entities/recyclability-result-dto';
import { LifeCycleResultDto } from '../entities/evaluation-entities/life-cycle-result-dto';
import { PackagingSystemLifeCycleCradleToGateResultDto } from '../entities/evaluation-entities/packaging-part-results/packaging-system-life-cycle-cradle-to-gate-result-dto';
import { PackagingUnitLifeCycleCradleToGateResultDto }   from '../entities/evaluation-entities/packaging-part-results/packaging-unit-life-cycle-cradle-to-gate-result-dto';
import { PackagingUnitRecyclabilityResultDto } from '../entities/evaluation-entities/packaging-part-results/packaging-unit-recyclability-result-dto';
import { PackagingSystemRecyclabilityResultDto } from '../entities/evaluation-entities/packaging-part-results/packaging-system-recyclability-result-dto';
import { PackagingSystemLifeCycleResultDto } from '../entities/evaluation-entities/packaging-part-results/packaging-system-life-cycle-result-dto';
import { PackagingUnitLifeCycleResultDto } from '../entities/evaluation-entities/packaging-part-results/packaging-unit-life-cycle-result-dto';
import { RecyclingStreamTranslationsDto } from '../entities/evaluation-entities/recycling-stream-dto';
import { ProblemInformationDto } from '../entities/evaluation-entities/problem-information-dto';
import { ExpenseResultDto, ExpenseResultsDto, PackagingSystemEntryExpenseResultsDto, PackagingSystemExpenseResultDto, PackagingSystemExpenseResultsDto, PackagingUnitEntryExpenseResultsDto, PackagingUnitExpenseResultDto, PackagingUnitExpenseResultsDto } from '../entities/evaluation-entities/expense-result-dto';
import { ExpenseCalculationCountryDto } from '../entities/evaluation-entities/expense-calculation-country-dto';

const UNITS_CONTROLLER = 'packaging-units';
const SYSTEMS_CONTROLLER = 'packaging-systems';

@Injectable({ providedIn: 'root' })
export class AnalysisApiService extends ParentApiService {

  constructor(private http: HttpClient) { super(); }

  private getControllerName(packagingPart: number): string {
    if (packagingPart !== PackagingPart.Unit && packagingPart !== PackagingPart.System) {
      throw new Error("AnalysisApiService: evaluations not implemented for current packaging level");
    }
    return packagingPart === PackagingPart.Unit ? UNITS_CONTROLLER : SYSTEMS_CONTROLLER;
  }

  public getRecyclabilityEvaluationResult(dto: EvaluationGenerationDto, packagingPart: number): Observable<RecyclabilityResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/evaluate-recyclability`;
    return this.http.post<RecyclabilityResultDto[]>(url, dto);
  }

  public getLifeCycleEvaluationResult(dto: EvaluationGenerationDto, packagingPart: number): Observable<LifeCycleResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/evaluate-lifecycle`;
    return this.http.post<LifeCycleResultDto[]>(url, dto);
  }

  public getCradleToGateEvaluationResult(dto: EvaluationGenerationDto, packagingPart: number): Observable<LifeCycleResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/evaluate-lifecycle/cradle-to-gate`;
    return this.http.post<LifeCycleResultDto[]>(url, dto);
  }

  public getPlasticTaxCalculationResult(dto: EvaluationGenerationDto, packagingPart: number): Observable<ExpenseResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/calculate-plastic-tax`;
    return this.http.post<ExpenseResultDto[]>(url, dto);
  }

  public getLicenseFeeCalculationResult(dto: EvaluationGenerationDto, packagingPart: number): Observable<ExpenseResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/calculate-license-fee`;
    return this.http.post<ExpenseResultDto[]>(url, dto);
  }

  public getEcomodulationCalculationResult(licenseFeeResultId: string, packagingPart: number): Observable<ExpenseResultDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    const url = `${environment.webApiAddress}/api/${controllerName}/calculate-ecomodulation?licenseFeeResultId=${licenseFeeResultId}`;
    return this.http.get<ExpenseResultDto[]>(url);
  }

  public getPlasticTaxCountries(): Observable<ExpenseCalculationCountryDto[]> {
    const url = `${environment.webApiAddress}/api/plastic-taxes/countries`;
    return this.http.get<ExpenseCalculationCountryDto[]>(url);
  }

  public getLicenseFeeCountries(): Observable<ExpenseCalculationCountryDto[]> {
    const url = `${environment.webApiAddress}/api/license-fees/countries`;
    return this.http.get<ExpenseCalculationCountryDto[]>(url);
  }

  public getEcomodulationCountries(): Observable<ExpenseCalculationCountryDto[]> {
    const url = `${environment.webApiAddress}/api/ecomodulation/countries`;
    return this.http.get<ExpenseCalculationCountryDto[]>(url);
  }

  private mapExpenseResults<T extends ExpenseResultDto>(licenseFeeResults: T[],
                            ecomodulationResults: T[],
                            plasticTaxResults: T[]): ExpenseResultsDto<T>[] {

    const resultsByCountry = new Map<string, ExpenseResultsDto<T>>();

    for (const licenseFeeResult of licenseFeeResults) {
      if (!resultsByCountry.has(licenseFeeResult.evaluatedCountry)) {
        resultsByCountry.set(licenseFeeResult.evaluatedCountry, new ExpenseResultsDto(licenseFeeResult.evaluatedCountry));
      }
      resultsByCountry.get(licenseFeeResult.evaluatedCountry)!.licenseFee = licenseFeeResult;
    }

    for (const ecomodulationResult of ecomodulationResults) {
      if (!resultsByCountry.has(ecomodulationResult.evaluatedCountry)) {
        resultsByCountry.set(ecomodulationResult.evaluatedCountry, new ExpenseResultsDto(ecomodulationResult.evaluatedCountry));
      }
      resultsByCountry.get(ecomodulationResult.evaluatedCountry)!.ecomodulation = ecomodulationResult;
    }

    for (const plasticTaxResult of plasticTaxResults) {
      if (!resultsByCountry.has(plasticTaxResult.evaluatedCountry)) {
        resultsByCountry.set(plasticTaxResult.evaluatedCountry, new ExpenseResultsDto(plasticTaxResult.evaluatedCountry));
      }
      resultsByCountry.get(plasticTaxResult.evaluatedCountry)!.plasticTax = plasticTaxResult;
    }

    return Array.from(resultsByCountry.values());
  }

  private mapMultipleExpenseResults<T extends ExpenseResultDto>(licenseFeeResults: T[][],
                            ecomodulationResults: T[][],
                            plasticTaxResults: T[][]): ExpenseResultsDto<T>[][] {

    const licenseFeeResultsByEvaluationTimestamp = new Map<string, T[]>();
    const ecomodulationResultsByEvaluationTimestamp = new Map<string, T[]>();
    const plasticTaxResultsByEvaluationTimestamp = new Map<string, T[]>();

    const timestamps = new Set<string>();

    for (const licenseFeeResult of licenseFeeResults) {
      const timestamp = licenseFeeResult[0].evaluationReferenceTimestamp;
      licenseFeeResultsByEvaluationTimestamp.set(timestamp, licenseFeeResult);
      timestamps.add(timestamp);
    }

    for (const ecomodulationResult of ecomodulationResults) {
      const timestamp = ecomodulationResult[0].evaluationReferenceTimestamp;
      ecomodulationResultsByEvaluationTimestamp.set(timestamp, ecomodulationResult);
      timestamps.add(timestamp);
    }

    for (const plasticTaxResult of plasticTaxResults) {
      const timestamp = plasticTaxResult[0].evaluationReferenceTimestamp;
      plasticTaxResultsByEvaluationTimestamp.set(timestamp, plasticTaxResult);
      timestamps.add(timestamp);
    }

    const results: ExpenseResultsDto<T>[][] = [];
    for (const timestamp of timestamps) {
      const licenseFeeResult = licenseFeeResultsByEvaluationTimestamp.get(timestamp);
      const ecomodulationResult = ecomodulationResultsByEvaluationTimestamp.get(timestamp);
      const plasticTaxResult = plasticTaxResultsByEvaluationTimestamp.get(timestamp);

      const result = this.mapExpenseResults(licenseFeeResult ?? [], ecomodulationResult ?? [], plasticTaxResult ?? []);
      results.push(result);
    }

    return results;
  }

  private setUnderlyingResults(packagingSystemResult: PackagingSystemExpenseResultsDto) {
    const packagingUnitChildren = Math.max(packagingSystemResult.ecomodulation?.packagingUnitResults.length ?? 0,
                                           packagingSystemResult.licenseFee?.packagingUnitResults.length ?? 0,
                                           packagingSystemResult.plasticTax?.packagingUnitResults.length ?? 0);

    const analyzedSystem = (packagingSystemResult.licenseFee ?? packagingSystemResult.ecomodulation ?? packagingSystemResult.plasticTax)?.analyzedPackagingSystem;
    if (analyzedSystem) {
      packagingSystemResult.analyzedPackagingSystem = analyzedSystem;
    }

    packagingSystemResult.packagingUnitResults = [];
    for (let i = 0; i < packagingUnitChildren; i++) {
      const licenseFeeResult = packagingSystemResult.licenseFee?.packagingUnitResults[i];
      const ecomodulationResult = packagingSystemResult.ecomodulation?.packagingUnitResults[i];
      const plasticTaxResult = packagingSystemResult.plasticTax?.packagingUnitResults[i];

      const referenceResult = licenseFeeResult ?? ecomodulationResult ?? plasticTaxResult;

      if (referenceResult) {
        const packagingUnitResult = new PackagingUnitExpenseResultsDto(referenceResult.evaluatedCountry);

        if (licenseFeeResult) {
          packagingUnitResult.licenseFee = licenseFeeResult.packagingUnitResult;
        }

        if (ecomodulationResult) {
          packagingUnitResult.ecomodulation = ecomodulationResult.packagingUnitResult;
        }

        if (plasticTaxResult) {
          packagingUnitResult.plasticTax = plasticTaxResult.packagingUnitResult;
        }

        const subResult = new PackagingUnitEntryExpenseResultsDto();
        subResult.analyzedPackagingUnitEntry = referenceResult.analyzedPackagingUnitEntry;
        subResult.packagingUnitResult = packagingUnitResult;
        packagingUnitResult.analyzedPackagingUnit = referenceResult.analyzedPackagingUnitEntry.underlyingPackagingUnit;

        packagingSystemResult.packagingUnitResults.push(subResult)
      }
    }

    const packagingSystemChildren = Math.max(packagingSystemResult.ecomodulation?.packagingSystemResults.length ?? 0,
    packagingSystemResult.licenseFee?.packagingSystemResults.length ?? 0,
    packagingSystemResult.plasticTax?.packagingSystemResults.length ?? 0);

    packagingSystemResult.packagingSystemResults = [];
    for (let i = 0; i < packagingSystemChildren; i++) {
      const licenseFeeResult = packagingSystemResult.licenseFee?.packagingSystemResults[i];
      const ecomodulationResult = packagingSystemResult.ecomodulation?.packagingSystemResults[i];
      const plasticTaxResult = packagingSystemResult.plasticTax?.packagingSystemResults[i];

      const referenceResult = licenseFeeResult ?? ecomodulationResult ?? plasticTaxResult;

      if (referenceResult) {
        const subPackagingSystemResult = new PackagingSystemExpenseResultsDto(referenceResult.evaluatedCountry);

        if (licenseFeeResult) {
          subPackagingSystemResult.licenseFee = licenseFeeResult.packagingSystemResult;
        }

        if (ecomodulationResult) {
          subPackagingSystemResult.ecomodulation = ecomodulationResult.packagingSystemResult;
        }

        if (plasticTaxResult) {
          subPackagingSystemResult.plasticTax = plasticTaxResult.packagingSystemResult;
        }

        const subResult = new PackagingSystemEntryExpenseResultsDto();
        subResult.analyzedPackagingSystemEntry = referenceResult.analyzedPackagingSystemEntry;
        subResult.packagingSystemResult = subPackagingSystemResult;
        subPackagingSystemResult.analyzedPackagingSystem = referenceResult.analyzedPackagingSystemEntry.underlyingPackagingSystem;

        this.setUnderlyingResults(subPackagingSystemResult);
        packagingSystemResult.packagingSystemResults.push(subResult);
      }
    }
  }

  private mapMultiplePackagingSystemExpenseResults(
    licenseFeeResults: PackagingSystemExpenseResultDto[][],
    ecomodulationResults: PackagingSystemExpenseResultDto[][],
    plasticTaxResults: PackagingSystemExpenseResultDto[][]): ExpenseResultsDto<PackagingSystemExpenseResultDto>[][] {
      const results = this.mapMultipleExpenseResults(licenseFeeResults, ecomodulationResults, plasticTaxResults);
      const packagingSystemResults = results as PackagingSystemExpenseResultsDto[][];
      for (const result of packagingSystemResults) {
        for (const countryResult of result) {
          this.setUnderlyingResults(countryResult)
        }
      }

      return results;
  }

  private mapMultiplePackagingUnitExpenseResults(
    licenseFeeResults: PackagingUnitExpenseResultDto[][],
    ecomodulationResults: PackagingUnitExpenseResultDto[][],
    plasticTaxResults: PackagingUnitExpenseResultDto[][]): ExpenseResultsDto<PackagingUnitExpenseResultDto>[][] {
      const results = this.mapMultipleExpenseResults(licenseFeeResults, ecomodulationResults, plasticTaxResults);
      const packagingUnitResults = results as PackagingUnitExpenseResultsDto[][];
      for (const result of packagingUnitResults) {
        for (const countryResult of result) {
          const analyzedUnit = (countryResult.licenseFee ?? countryResult.ecomodulation ?? countryResult.plasticTax)?.analyzedPackagingUnit;

          if (analyzedUnit) {
            countryResult.analyzedPackagingUnit = analyzedUnit;
          }
        }
      }

      return results;
  }

  public getExpenseCalculationResults(dto: EvaluationGenerationDto, packagingPart: number)
      : Observable<ExpenseResultsDto<ExpenseResultDto>[]> {
    const observableLicenseFee = this.getLicenseFeeCalculationResult(dto, packagingPart);

    const observablelicenceFeeEcomodulationPair = observableLicenseFee.pipe(
      mergeMap(licenseFeeResult => {
        const firstLicenseFeeResult = licenseFeeResult[0];
        const observableEcomodulation = this.getEcomodulationCalculationResult(firstLicenseFeeResult.id, packagingPart);

        return observableEcomodulation.pipe(
          map(ecomodulationResult => [licenseFeeResult, ecomodulationResult])
        )
      })
    );

    const observablePlasticTax = this.getPlasticTaxCalculationResult(dto, packagingPart);

    return forkJoin([observablelicenceFeeEcomodulationPair, observablePlasticTax])
      .pipe(map(([[licenseFeeResult, ecomodulationResult], plasticTaxResult]) => {
        return this.mapExpenseResults(licenseFeeResult, ecomodulationResult, plasticTaxResult);
      })
    );
  }

  public getPackagingUnitExpenseCalculationHistoryResults(id: number, versionId: number)
      : Observable<ExpenseResultsDto<PackagingUnitExpenseResultDto>[][]> {
    const observableLicenseFee = this.getPackagingUnitLicenseFeeHistoryResults(id, versionId);
    const observableEcomodulation = this.getPackagingUnitEcomodulationHistoryResults(id, versionId);
    const observablePlasticTax = this.getPackagingUnitPlasticTaxHistoryResults(id, versionId);

    return forkJoin([observableLicenseFee, observableEcomodulation, observablePlasticTax])
      .pipe(map(([licenseFeeResult, ecomodulationResult, plasticTaxResult]) => {
        return this.mapMultiplePackagingUnitExpenseResults(licenseFeeResult, ecomodulationResult, plasticTaxResult);
      })
    );
  }

  public getPackagingSystemExpenseCalculationHistoryResults(id: number, versionId: number)
      : Observable<ExpenseResultsDto<PackagingSystemExpenseResultDto>[][]> {
    const observableLicenseFee = this.getPackagingSystemLicenseFeeHistoryResults(id, versionId);
    const observableEcomodulation = this.getPackagingSystemEcomodulationHistoryResults(id, versionId);
    const observablePlasticTax = this.getPackagingSystemPlasticTaxHistoryResults(id, versionId);

    return forkJoin([observableLicenseFee, observableEcomodulation, observablePlasticTax])
      .pipe(map(([licenseFeeResult, ecomodulationResult, plasticTaxResult]) => {
        return this.mapMultiplePackagingSystemExpenseResults(licenseFeeResult, ecomodulationResult, plasticTaxResult);
      })
    );
  }

  public getPackagingUnitRecyclabilityHistoryResults(id: number, versionId: number): Observable<PackagingUnitRecyclabilityResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/recyclability?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitRecyclabilityResultDto[][]>(url);
  }

  public getPackagingUnitPlasticTaxHistoryResults(id: number, versionId: number): Observable<PackagingUnitExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/plastic-tax?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitExpenseResultDto[][]>(url);
  }

  public getPackagingUnitLicenseFeeHistoryResults(id: number, versionId: number): Observable<PackagingUnitExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/license-fee?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitExpenseResultDto[][]>(url);
  }

  public getPackagingUnitEcomodulationHistoryResults(id: number, versionId: number): Observable<PackagingUnitExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/ecomodulation?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitExpenseResultDto[][]>(url);
  }

  public getPackagingSystemEcomodulationHistoryResults(id: number, versionId: number): Observable<PackagingSystemExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/ecomodulation?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemExpenseResultDto[][]>(url);
  }

  public getPackagingSystemRecyclabilityHistoryResults(
    id: number, versionId: number
  ): Observable<PackagingSystemRecyclabilityResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/recyclability?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemRecyclabilityResultDto[][]>(url);
  }

  public getPackagingUnitLifeCycleHistoryResults(id: number, versionId: number): Observable<PackagingUnitLifeCycleResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/lifecycle?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitLifeCycleResultDto[][]>(url);
  }

  public getPackagingSystemLifeCycleHistoryResults(id: number, versionId: number): Observable<PackagingSystemLifeCycleResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/lifecycle?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemLifeCycleResultDto[][]>(url);
  }

  public getPackagingUnitLifeCycleCradleToGateHistoryResults(id: number, versionId: number): Observable<PackagingUnitLifeCycleCradleToGateResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluation/history/lifecycle/cradle-to-gate?id=${id}&version=${versionId}`;
    return this.http.get<PackagingUnitLifeCycleCradleToGateResultDto[][]>(url);
  }

  public getPackagingSystemLifeCycleCradleToGateHistoryResults(id: number, versionId: number): Observable<PackagingSystemLifeCycleCradleToGateResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/lifecycle/cradle-to-gate?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemLifeCycleCradleToGateResultDto[][]>(url);
  }

  public getPackagingSystemLicenseFeeHistoryResults(id: number, versionId: number): Observable<PackagingSystemExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/license-fee?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemExpenseResultDto[][]>(url);
  }

  public getPackagingSystemPlasticTaxHistoryResults(id: number, versionId: number): Observable<PackagingSystemExpenseResultDto[][]> {
    const url = `${environment.webApiAddress}/api/packaging-systems/evaluation/history/plastic-tax?id=${id}&version=${versionId}`;
    return this.http.get<PackagingSystemExpenseResultDto[][]>(url);
  }

  public generateLcaExcel(id: number, versionId: number, analysisVersionId: string, packagingPart: number) {
    const controllerName = this.getControllerName(packagingPart);
    let url = `${environment.webApiAddress}/api/${controllerName}/evaluate-lifecycle/excel/${id}?version=${versionId}`;
    if (analysisVersionId != null && analysisVersionId !== '') {
      url = url.concat(`&evaluationId=${analysisVersionId}`);
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  public getRecyclabilityCreditsRequirement(id: number, packagingPart: number, versionId?: number) {
    const controllerName = this.getControllerName(packagingPart);
    let url = `${environment.webApiAddress}/api/${controllerName}/recyclability-evaluation-costs?id=${id}`;
    if (versionId != null) {
      url = url.concat(`&version=${versionId}`);
    }
    return this.http.get<number>(url);
  }

  public getLifeCycleCreditsRequirement(id: number, packagingPart: number, versionId?: number) {
    const controllerName = this.getControllerName(packagingPart);
    let url = `${environment.webApiAddress}/api/${controllerName}/lifecycle-evaluation-costs?id=${id}`;
    if (versionId != null) {
      url = url.concat(`&version=${versionId}`);
    }
    return this.http.get<number>(url);
  }

  public getExpenseCreditsRequirement(id: number, packagingPart: number, versionId?: number) {
    const controllerName = this.getControllerName(packagingPart);
    let url = `${environment.webApiAddress}/api/${controllerName}/expense-calculation-costs?id=${id}`;
    if (versionId != null) {
      url = url.concat(`&version=${versionId}`);
    }
    return this.http.get<number>(url);
  }

  public isLifeCycleAnalysisPossible(
    id: number, ignoreDistributionCountries: boolean, packagingPart: number, version?: number
  ): Observable<boolean> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    params = params.set('ignoreDistributionCountries', ignoreDistributionCountries);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/is-lifecycle-analysis-possible`;
    return this.http.get<boolean>(url, { params });
  }

  public isRecyclabilityAnalysisPossible(
    id: number, ignoreDistributionCountries: boolean, packagingPart: number, version?: number
  ): Observable<boolean> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    params = params.set('ignoreDistributionCountries', ignoreDistributionCountries);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/is-recyclability-analysis-possible`;
    return this.http.get<boolean>(url, { params });
  }

  public isExpenseAnalysisPossible(
    id: number, ignoreDistributionCountries: boolean, packagingPart: number, version?: number
  ): Observable<boolean> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    params = params.set('ignoreDistributionCountries', ignoreDistributionCountries);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/is-expense-calculation-possible`;
    return this.http.get<boolean>(url, { params });
  }

  public getExpenseAnalysisProblemInformation(
    id: number, packagingPart: number, version?: number
  ): Observable<ProblemInformationDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/expense-calculation-problem-information`;
    return this.http.get<ProblemInformationDto[]>(url, { params });
  }

  public isLifeCycleAnalysisPossibleImport(
    candidateId: number, packagingPart: number, ignoreDistributionCountries = true
  ): Observable<boolean> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('importCandidateId', candidateId);
    params = params.set('ignoreDistributionCountries', ignoreDistributionCountries);
    const url = `${environment.webApiAddress}/api/${controllerName}/is-lifecycle-analysis-possible/by-import-candidate`;
    return this.http.get<boolean>(url, { params });
  }

  public isRecyclabilityAnalysisPossibleImport(
    candidateId: number, packagingPart: number, ignoreDistributionCountries = true
  ): Observable<boolean> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('importCandidateId', candidateId);
    params = params.set('ignoreDistributionCountries', ignoreDistributionCountries);
    const url = `${environment.webApiAddress}/api/${controllerName}/is-recyclability-analysis-possible/by-import-candidate`;
    return this.http.get<boolean>(url, { params });
  }

  public getLifeCycleAnalysisProblemInformation(
    id: number, packagingPart: number, version?: number
  ): Observable<ProblemInformationDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/life-cycle-analysis-problem-information`;
    return this.http.get<ProblemInformationDto[]>(url, { params });
  }

  public getRecyclabilityAnalysisProblemInformation(
    id: number, packagingPart: number, version?: number
  ): Observable<ProblemInformationDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    const url = `${environment.webApiAddress}/api/${controllerName}/recyclability-analysis-problem-information`;
    return this.http.get<ProblemInformationDto[]>(url, { params });
  }

  public getLifeCycleAnalysisProblemInformationImport(
    candidateId: number, packagingPart: number
  ): Observable<ProblemInformationDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('importCandidateId', candidateId);
    const url = `${environment.webApiAddress}/api/${controllerName}/life-cycle-analysis-problem-information/by-import-candidate`;
    return this.http.get<ProblemInformationDto[]>(url, { params });
  }

  public getRecyclabilityAnalysisProblemInformationImport(
    candidateId: number, packagingPart: number
  ): Observable<ProblemInformationDto[]> {
    const controllerName = this.getControllerName(packagingPart);
    let params: HttpParams = new HttpParams();
    params = params.set('importCandidateId', candidateId);
    const url = `${environment.webApiAddress}/api/${controllerName}/recyclability-analysis-problem-information/by-import-candidate`;
    return this.http.get<ProblemInformationDto[]>(url, { params });
  }

  public setAnalysisViewedTimestamp(analysisId: string, stayOnPageIfError?: boolean) {
    const url = `${environment.webApiAddress}/api/validation/update-last-access-timestamp?analysisId=${analysisId}`;
    return this.http.post<any>(url, null, { headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader });
  }

  public getRecyclingStreams(): Observable<RecyclingStreamTranslationsDto[]> {
    const url = `${environment.webApiAddress}/api/recycling-streams/translations`;
    return this.http.get<RecyclingStreamTranslationsDto[]>(url);
  }
}
